import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { productService } from '../../services/productService';

interface Suggestion {
  code: string;
  type: 'DTK' | 'OEM';
  sku: string;
}

interface Props {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  testid?: string;
  className?: string;
  size?: 'default' | 'sm';
  /** Invocata quando l'utente seleziona un suggerimento (click o Invio su
   * una voce evidenziata) — riceve il codice appena selezionato (evita che
   * il chiamante legga lo stato precedente/stale prima del re-render). */
  onSelect?: (code: string) => void;
  /** Invocata quando si preme Invio e non c'è una voce del dropdown da selezionare (es. avvia la ricerca del componente padre). */
  onSubmit?: () => void;
}

/**
 * Autocompletamento leggero per il campo "Cod. DTK/OEM" — usato sia nel
 * widget "Ricerca avanzata" (home) sia nella barra filtri del catalogo
 * (richiesta utente 24/02/2026). A differenza della ricerca navbar
 * (`SearchCodici`), qui il dropdown mostra SOLO i codici che matchano
 * (DTK o OEM), non la card completa del prodotto: la selezione riempie
 * il campo con il codice esatto, la ricerca/filtro resta a carico del
 * bottone "Cerca" del componente padre.
 */
export function CodiceOemAutocomplete({
  value, onChange, placeholder = 'Cod. DTK/OEM', testid, className = '', size = 'default', onSelect, onSubmit,
}: Props) {
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reqIdRef = useRef(0);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = value.trim();
    if (q.length < 2) { setResults([]); setLoading(false); return; }
    setLoading(true);
    const myReqId = ++reqIdRef.current;
    debounceRef.current = setTimeout(() => {
      productService.list({ q, limit: 8, publicOnly: true, codeOnly: true, expandOemMatches: true })
        .then(resp => {
          if (myReqId !== reqIdRef.current) return;
          const seen = new Set<string>();
          const suggestions: Suggestion[] = [];
          for (const p of resp.items) {
            const code = p.matchedOemCode || p.dtkCodice || p.sku;
            const type: 'DTK' | 'OEM' = p.matchedOemCode ? 'OEM' : 'DTK';
            const key = `${p.sku}-${code}`;
            if (seen.has(key)) continue;
            seen.add(key);
            suggestions.push({ code, type, sku: p.sku });
          }
          setResults(suggestions);
          setSelectedIdx(0);
        })
        .catch(() => { if (myReqId === reqIdRef.current) setResults([]); })
        .finally(() => { if (myReqId === reqIdRef.current) setLoading(false); });
    }, 200);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [value]);

  const handleSelect = (s: Suggestion) => {
    onChange(s.code);
    setOpen(false);
    setResults([]);
    onSelect?.(s.code);
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIdx(i => Math.min(i + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') {
      if (open && results[selectedIdx]) {
        e.preventDefault();
        handleSelect(results[selectedIdx]);
      } else if (onSubmit) {
        e.preventDefault();
        setOpen(false);
        onSubmit();
      }
      // Altrimenti (nessun onSubmit, es. campo dentro un <form>): lascia
      // il comportamento nativo di submit del form del componente padre.
    }
    else if (e.key === 'Escape') { setOpen(false); inputRef.current?.blur(); }
  };

  const isSm = size === 'sm';
  const showDropdown = open && value.trim().length >= 2;

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {isSm ? (
        <div className={`flex items-center gap-1.5 h-9 px-2.5 rounded-lg border bg-white transition-colors ${open ? 'ring-2 ring-[hsl(214,58%,41%)]/20 border-[hsl(214,58%,41%)]' : 'focus-within:ring-2 focus-within:ring-[hsl(214,58%,41%)]/20 focus-within:border-[hsl(214,58%,41%)]'}`}>
          {loading
            ? <Loader2 className="w-3 h-3 text-muted-foreground flex-shrink-0 animate-spin" />
            : <Search className="w-3 h-3 text-muted-foreground flex-shrink-0" />
          }
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={e => { onChange(e.target.value); setOpen(true); }}
            onFocus={() => { if (value.trim().length >= 2) setOpen(true); }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            data-testid={testid}
            autoComplete="off"
            aria-label={placeholder}
            className="flex-1 h-full text-xs outline-none bg-transparent placeholder:text-muted-foreground min-w-0"
          />
        </div>
      ) : (
        <>
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={e => { onChange(e.target.value); setOpen(true); }}
            onFocus={() => { if (value.trim().length >= 2) setOpen(true); }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            data-testid={testid}
            autoComplete="off"
            aria-label={placeholder}
            className="w-full h-11 pl-3 pr-9 rounded-lg border bg-white text-sm font-medium text-foreground placeholder:text-muted-foreground placeholder:font-medium placeholder:uppercase placeholder:tracking-wide focus:outline-none focus:ring-2 focus:ring-[hsl(214,58%,41%)] focus:border-transparent"
          />
          {/* Vedi la nota in RMASearchPage: il transform di centratura non
              può stare sull'icona che gira, altrimenti ``animate-spin``
              interpola da quel transform e l'icona scende invece di ruotare. */}
          {loading
            ? (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                <Loader2 className="w-4 h-4 text-emerald-600 animate-spin" />
              </span>
            )
            : <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-600 pointer-events-none" />
          }
        </>
      )}

      {showDropdown && (
        <div
          className="absolute top-full left-0 right-0 mt-1.5 bg-white border border-gray-200 rounded-xl shadow-2xl z-[200] overflow-hidden"
          data-testid={testid ? `${testid}-dropdown` : undefined}
        >
          {!loading && results.length === 0 ? (
            <div className="px-4 py-3.5 text-center">
              <p className="text-sm text-muted-foreground">Nessun codice trovato</p>
            </div>
          ) : (
            <div className="max-h-64 overflow-y-auto">
              {results.map((s, idx) => (
                <button
                  key={`${s.sku}-${s.code}`}
                  type="button"
                  data-testid={testid ? `${testid}-option-${idx}` : undefined}
                  onClick={() => handleSelect(s)}
                  onMouseEnter={() => setSelectedIdx(idx)}
                  className={`w-full flex items-center justify-between gap-2 text-left px-4 py-2.5 text-sm font-mono font-medium transition-colors border-b border-gray-100 last:border-0 ${
                    idx === selectedIdx ? 'bg-[hsl(214,58%,50%)] text-white' : 'text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  <span>{s.code}</span>
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded flex-shrink-0 ${
                    idx === selectedIdx ? 'bg-white/20 text-white' : s.type === 'DTK' ? 'bg-[hsl(214,58%,91%)] text-[hsl(214,58%,41%)]' : 'bg-amber-100 text-amber-700'
                  }`}>
                    {s.type}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
